<template>
  <form
    class="d-books-search-form"
    @submit.stop.prevent="searchTextUpdated()"
  >
    <button
      class="hidden-submit-button"
      type="submit"
    />

    <div class="grid-flex grid-middle grid-no-response visible-until-sm padding-bottom-2em">
      <div class="grid-item">
        <DuiIconSearchFilters />
        <strong class="valign-middle">Search filters</strong>
      </div>
      <div class="grid-item-shrink">
        <a @click="toggleMoreFilters()">
          <span v-if="isMoreOptionsExpanded">Hide filters</span>
          <span v-if="!isMoreOptionsExpanded">Show all</span>
        </a>
      </div>
    </div>

    <DuiDropdown
      ref="genresDropdown"
      :disabled="isProcessing"
      label="Genre"
      :label-width="64"
      :multiple="true"
      name="genre"
      :nested="true"
      placeholder="Select genre"
      :search="true"
      :store-load-key="`${ storeSearchPrefix }/ReviewerSearch/Genres/available-genres`"
      :store-update-key="`${ storeSearchPrefix }/ReviewerSearch/Genres/selectGenre`"
    />

    <div
      class="more-filters"
      :class="{ 'expanded': isMoreOptionsExpanded }"
    >
      <DuiDropdown
        ref="sortByDropdown"
        v-model="search.sortBy"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Sort by"
        :label-width="64"
        name="sortBy"
        @change="searchParamUpdated('sortBy')"
      >
        <template slot="options">
          <DuiOption
            text="Recommended"
            value="recommended"
          />
          <DuiOption
            text="Followers"
            value="followers"
          />
        </template>
      </DuiDropdown>

      <div
        class="input-group space-top-2em"
        :disabled="isProcessing"
      >
        <input
          v-model="search.text"
          class="input-full"
          placeholder="Search by keyword"
          type="text"
        >
        <DuiIconSearch class="space-right-1em" />
      </div>

      <DuiDropdown
        ref="reviewFormatDropdown"
        v-model="search.reviewFormat"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Format"
        :label-width="64"
        name="reviewFormat"
        placeholder="Review type..."
        @change="searchParamUpdated('reviewFormat')"
      >
        <template slot="options">
          <DuiOption
            text="Written"
            value="written"
          />
          <DuiOption
            text="Video"
            value="video"
          />
          <DuiOption
            text="Written and Video"
            value="both"
          />
        </template>
      </DuiDropdown>

      <div class="space-top-2em">
        <DuiCheckbox
          v-model="search.directSubmissions"
          :disabled="isProcessing"
          label="Accepts direct submissions"
          name="Accepts direct submissions"
          title="Accepts direct submissions"
          @change="searchParamUpdated('directSubmissions')"
        />
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue';

import directorySearchService from '@d-services/reviewer-search-service/directory-search-service';
import formMixin from '@d-shared/mixins/form-mixin';
import genresService from '@d-services/genres-service/genres-service';
import { getErrorMessage } from '@d-shared/utils/errors';
import HistoryHelper from '@d-helpers/history-helper';
import SearchHelper from '@d-helpers/search-helper';
import UrlParamsHelper from '@d-helpers/url-params-helper';
import URLS from '@d-shared/urls/urls';

export default {
  mixins: [
    formMixin,
  ],
  props: {
    storeSearchPrefix: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isLoadingUrlParams: false,
    isMoreOptionsExpanded: false,
    search: {
      sortBy: null,
      text: null,
      directSubmissions: false,
      reviewFormat: null,
    },
    textTagId: 0,
  }),
  computed: {
    storeSearchTags() {
      return this.$store.getters[`${ this.storeSearchPrefix }/ReviewerSearch/get-all-search-tags`];
    },
    storeSelectedGenres() {
      return this.$store.getters[`${ this.storeSearchPrefix }/ReviewerSearch/Genres/selected-genres`];
    },
  },
  watch: {
    storeSearchTags: {
      handler: function() {
        this.storeSearchTagsUpdated();
      },
      deep: true,
    },
    storeSelectedGenres: function() {
      this.searchParamUpdated('genres');
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    addSearchTag(searchTag, performSearch) {
      if (!performSearch || this.isLoadingUrlParams) {
        this.$store.dispatch(`${ this.storeSearchPrefix }/ReviewerSearch/addSearchTag`, searchTag);
        return;
      }

      this.$store.dispatch(`${ this.storeSearchPrefix }/ReviewerSearch/addSearchTagAndSearch`, searchTag);
    },

    getGenres() {
      const genres = this.storeSelectedGenres.map(genre => genre.value);
      return genres.length ? genres : null;
    },

    getGenreTags() {
      return this.storeSelectedGenres.map((genre) => ({
        key: `genre-${ genre.id }`,
        option: genre,
        parentKey: 'genres',
        text: genre.text,
        value: genre.value,
      }));
    },

    getPageTag(pageNumber) {
      return  {
        hidden: true,
        key: 'page',
        page: true,
        text: `Page ${ pageNumber }`,
        value: (pageNumber !== 1) ? pageNumber : null,
      };
    },

    getSortBy() {
      if (this.search.sortBy === 'recommended') {
        return null;
      }

      return this.search.sortBy;
    },

    getSortByTagText() {
      if (this.search.sortBy === 'followers') {
        return 'Sort by: followers';
      }

      return null;
    },

    getReviewFormat() {
      return this.search.reviewFormat;
    },

    getReviewFormatTagText() {
      if (!this.search.reviewFormat) return null;
      const formatString = this.search.reviewFormat === 'both' ? 'written and video' : this.search.reviewFormat;
      return `Review format: ${formatString}`;
    },

    getSearchTag(key) {
      switch(key) {
        case 'genres':
          return {
            key: key,
            query: 'genre_ids',
            tags: this.getGenreTags(),
            value: this.getGenres(),
          };
        case 'sortBy':
          return {
            key: key,
            order: true,
            text: this.getSortByTagText(),
            value: this.getSortBy(),
          };
        case 'directSubmissions':
          return {
            key: key,
            query: 'accepting_direct_submissions',
            text: 'Accepts direct submissions',
            value: this.search[key] || null,
          };
        case 'reviewFormat':
          return {
            key: key,
            query: 'review_format',
            text: this.getReviewFormatTagText(),
            value: this.getReviewFormat(),
          };
        default:
          return null;
      };
    },

    async init() {
      await this.loadOptions();
      await this.loadUrlParams();
    },

    async loadOptions() {
      this.isLoading = true;

      try {
        const genres = await genresService.getGenres(true);

        this.$store.dispatch(
          `${ this.storeSearchPrefix }/ReviewerSearch/Genres/setAvailableGenres`,
          JSON.parse(JSON.stringify(genres)),
        );
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      await this.$nextTick();

      this.isLoading = false;
    },

    async loadUrlParams() {
      this.$set(this, 'isLoadingUrlParams', true);

      const queryParams = UrlParamsHelper.getParamsFromCurrentUrl();
      const paramKeys = Object.keys(queryParams);

      if (!paramKeys.includes('sortBy')) {
        this.$set(this.search, 'sortBy', 'recommended');
      }

      paramKeys.forEach(key => {
        const currentParams = queryParams[key];

        switch (key) {
          case 'genres':
            this.selectInitialGenres(currentParams);
            break;
          case 'term':
            this.$set(this.search, 'text', currentParams.join(' '));
            this.searchTextUpdated(false);
            break;
          case 'page':
            const pageTag = this.getPageTag(currentParams[0]);
            this.addSearchTag(pageTag, false);
            break;
          default:
            this.$set(this.search, key, currentParams[0]);
            this.searchParamUpdated(key, false);
            break;
        }
      });

      await this.$nextTick();
      directorySearchService.search();
      this.$set(this, 'isLoadingUrlParams', false);
    },

    removeSearchTag(searchTag) {
      this.$store.dispatch(`${ this.storeSearchPrefix }/ReviewerSearch/removeSearchTag`, searchTag);
    },

    selectInitialGenres(genreIds) {
      this.$store.dispatch(`${ this.storeSearchPrefix }/ReviewerSearch/Genres/setInitiallySelectedGenres`, genreIds);
    },

    toggleMoreFilters() {
      this.isMoreOptionsExpanded = !this.isMoreOptionsExpanded;
    },

    updateCheckboxFromTag(fieldName) {
      const tag = this.storeSearchTags.find(t => t.key === fieldName);
      if (!tag) {
        this.updateSearchFieldValue(fieldName, false);
        return;
      }

      this.updateSearchFieldValue(fieldName, true);
    },

    updateCurrentUrl() {
      let newUrl = URLS.app.directories;

      let tags = {
        term: [],
      };

      const genres = this.storeSearchTags.filter(t => t.parentKey === 'genres');
      if (genres.length) {
        tags.genres = genres.map(g => g.value);
      }

      const otherTags = this.storeSearchTags.filter(t => t.parentKey !== 'genres');

      otherTags.forEach(t => {
        if (t.isTerm === true) {
          tags.term.push(t.text);
          return;
        }

        tags[t.key] = t.value;
      });

      const searchParams = UrlParamsHelper.getSearchFromParams(tags);
      if (searchParams) {
        newUrl = `${ newUrl }?${ searchParams }`;
      }

      HistoryHelper.updateEntry(null, '', newUrl);
    },

    updateGenres() {
      const tags = this.storeSearchTags.filter(t => t.parentKey === 'genres');
      if (!tags.length && this.storeSelectedGenres.length) {
        this.$refs.genresDropdown.deselectAll();
        return;
      }

      const taggedGenres = tags.map(t => t.option);
      this.storeSelectedGenres.forEach((genre) => {
        if (!taggedGenres.includes(genre)) {
          this.$set(genre, 'selected', false);
          this.$store.dispatch(`${ this.storeSearchPrefix }/ReviewerSearch/Genres/selectGenre`, genre);
        }
      });
    },

    updateSearchFieldValue(fieldName, value) {
      if (this.search[fieldName] === value) {
        return;
      }

      this.$set(this.search, fieldName, value);
    },

    updateSortBy() {
      const tag = this.storeSearchTags.find(t => t.key === 'sortBy');
      if (tag && tag.value === this.search.sortBy) {
        return;
      }

      if (!tag && this.search.sortBy === 'recommended') {
        return;
      }

      this.$refs.sortByDropdown.deselectAll();

      if (!tag) {
        this.updateSearchFieldValue('sortBy', 'recommended');
        return;
      }

      this.updateSearchFieldValue('sortBy', tag.value);
    },

    updateReviewFormat() {
      const tag = this.storeSearchTags.find(t => t.key === 'reviewFormat');
      if (tag && tag.value === this.search.reviewFormat) {
        return;
      }

      this.$refs.reviewFormatDropdown.deselectAll();

      if (!tag) {
        return;
      }

      this.updateSearchFieldValue('reviewFormat', tag.value);
    },

    // Events
    searchParamUpdated(key, performSearch = true) {
      const searchTag = this.getSearchTag(key);
      if (!searchTag) {
        return;
      }

      if (searchTag.value === null) {
        this.removeSearchTag(searchTag);
        return;
      }

      this.addSearchTag(searchTag, performSearch);
    },

    searchTextUpdated(performSearch = true) {
      if (!this.search.text) {
        return;
      }

      const terms = SearchHelper.getTermsFromText(this.search.text);
      if (terms) {
        terms.forEach((term) => {
          this.addSearchTag({
            isTerm: true,
            key: `text-${ this.textTagId++ }`,
            query: null,
            text: term,
            value: term,
          }, false);
        });

        this.search.text = '';

        if (performSearch) {
          directorySearchService.search();
        }
      }
    },

    storeSearchTagsUpdated() {
      if (this.isLoadingUrlParams) {
        return;
      }
      this.updateGenres();
      this.updateSortBy();
      this.updateReviewFormat();
      this.updateCheckboxFromTag('directSubmissions');

      this.updateCurrentUrl();
    },
    // END Events
  },
};
</script>
